import type { ListingsAPIResponseBody } from '@noths/polaris-client-listings-api';
import { isBrowser } from '@noths/polaris-client-utils';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { adaptResponseFacetsToStore } from 'src/adapters/serviceResponseToStore/listings-api/adaptResponseFacetsToStore';
import { adaptResponseProductsToStore } from 'src/adapters/serviceResponseToStore/listings-api/adaptResponseProductsToStore';
import { adaptStoreToRequestBody } from 'src/adapters/storeToServiceRequest/listings-api/adaptStoreToRequestBody';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type {
  RequestProductsActionPayload,
  RequestProductsMetadata,
  TriggerName,
} from 'src/redux/products/types';
import { getListingsData } from 'src/services/listings-api/getListingsData';
import { PRODUCTS_LIST_DATA_FETCHING_ERROR } from './thunkErrors';

export interface RequestProductsOptions {
  healthCheck?: boolean;
  metadata?: RequestProductsMetadata;
  triggerName?: TriggerName;
}

export const requestProducts = createAsyncThunk<
  RequestProductsActionPayload,
  RequestProductsOptions
>('products/requestProducts', async ({ healthCheck, metadata, triggerName }, { getState }) => {
  const state = getState() as ReduxApplicationState;

  let listingsDataPromise: Promise<ListingsAPIResponseBody | null>;

  if (healthCheck) {
    listingsDataPromise = import('src/services/listings-api/healthCheckResponseBody').then(
      (mod) => mod.healthCheckResponseBody,
    );
  } else {
    listingsDataPromise = getListingsData({
      body: adaptStoreToRequestBody(state),
      bot: state.user.bot || false,
      userId: state.user.cognitoID || '',
      visitorId: state.user.gaVisitorId,
      servingConfig: state.productsQuery.servingConfig,
    });
  }

  const listingsControlsConfigPromise = import(
    'src/adapters/configToStore/listings-api/listingsControlsConfig'
  ).then((mod) => mod.listingsControlsConfig);

  const [data, listingsControlsConfig] = await Promise.all([
    listingsDataPromise,
    listingsControlsConfigPromise,
  ]);

  if (!data) {
    throw PRODUCTS_LIST_DATA_FETCHING_ERROR;
  }

  return {
    facets: adaptResponseFacetsToStore({
      facetConfig: listingsControlsConfig.facets,
      listingsFacets: data.facets,
      storeFacets: state.productsQuery.facets,
      removeZeroCountFacets: !isBrowser(),
    }),
    products: adaptResponseProductsToStore({
      products: data.products,
      pagination: data.pagination,
      attributionToken: data.metadata.attributionToken,
      term: state.productsQuery.searchTerm!,
      currencyCode: state.user.currencyCode,
      deliveryZone: state.user.deliveryZone!,
    }),
    triggerName,
    metadata,
  };
});
